import React, { type ReactElement, useCallback, useRef, useState } from 'react';

import Badge from '@atlaskit/badge';
import {
	CheckboxOption,
	type FormatOptionLabelMeta,
	type InputActionMeta,
	type OptionType,
	PopupSelect,
} from '@atlaskit/select';

import { type FilterOrControlId } from '../../../constants/filters';
import { FilterButton } from '../filter-button';

import { Control } from './control';
import { MenuList } from './menu-list';

type Options = OptionType[] | { label: string; options: OptionType[] }[];

export type SelectFilterOption = {
	avatarUrl?: string;
	isDefault: boolean;
	isSelected: boolean;
	isSuggested: boolean;
	label: string;
	queryParamValue: string;
	trackingKey: string;
	value: string;
};

type SelectFilterProps = {
	canClearFilter?: boolean;
	checkboxIndicators?: boolean;
	clearOptionsDisabled?: boolean;
	defaultIsOpen?: boolean;
	filterOrControlId: FilterOrControlId;
	formatOptionLabel?: (
		data: OptionType,
		formatOptionLabelMeta: FormatOptionLabelMeta<OptionType>,
	) => React.ReactNode;
	getFirstSelectedLabel?: (selected: OptionType) => string;
	isLoading?: boolean;
	isSearchable?: boolean;
	label: string;
	minMenuWidth?: number | string;
	noOptionsMessage?: string;
	onClearOptions?: () => void;
	onDropdownClose?: () => void;
	onDropdownOpen?: () => void;
	onSearchInputChange?: (value: string) => void;
	onSelectChange: (value: SelectFilterOption | SelectFilterOption[]) => void;
	options: Options;
	placeholder?: string;
	searchInput?: string;
	selectedOptions: SelectFilterOption[];
	testId?: string;
};

const SelectFilter = ({
	defaultIsOpen = false,
	isSearchable = false,
	isLoading = false,
	clearOptionsDisabled = false,
	canClearFilter = false,
	checkboxIndicators = false,

	formatOptionLabel,
	placeholder,
	searchInput,
	onSearchInputChange,
	onSelectChange,
	onDropdownOpen,
	onDropdownClose,
	options,
	selectedOptions,
	label,
	noOptionsMessage,
	filterOrControlId,
	getFirstSelectedLabel,
	onClearOptions,
	minMenuWidth,
	testId,
}: SelectFilterProps): React.JSX.Element => {
	const numSelectedOptions = selectedOptions.length;
	const firstSelected = selectedOptions[0];

	const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

	// TODO: this can be removed if we have debounce in the store when lookup
	const [currentSearchInput, setCurrentSearchInput] = useState(searchInput);

	const handleInputChange = useCallback(
		(value: string, action: InputActionMeta) => {
			if (action.action !== 'input-change') {
				return;
			}
			setCurrentSearchInput(value);

			if (!onSearchInputChange) {
				return;
			}

			if (debounceTimeout.current) {
				clearTimeout(debounceTimeout.current);
			}

			debounceTimeout.current = setTimeout(() => {
				onSearchInputChange(value);
			}, 300);
		},
		[onSearchInputChange],
	);

	const handleChange = useCallback(
		(value: OptionType | readonly OptionType[]) => {
			onSelectChange(value as SelectFilterOption | SelectFilterOption[]);
		},
		[onSelectChange],
	);

	return (
		<PopupSelect
			placeholder={placeholder}
			onMenuOpen={onDropdownOpen}
			onMenuClose={() => {
				setCurrentSearchInput('');
				onDropdownClose?.();
			}}
			minMenuWidth={minMenuWidth}
			maxMenuWidth="22rem"
			maxMenuHeight={400}
			menuPlacement="bottom"
			defaultIsOpen={defaultIsOpen}
			closeMenuOnSelect={!checkboxIndicators}
			isSearchable={isSearchable}
			searchThreshold={-1}
			blurInputOnSelect={false}
			tabSelectsValue={false}
			backspaceRemovesValue={false}
			hideSelectedOptions={false}
			controlShouldRenderValue={false}
			isClearable={false}
			// @ts-ignore - https://product-fabric.atlassian.net/browse/DSP-21000
			defaultOptions={false}
			isLoading={isLoading}
			clearOptionsDisabled={clearOptionsDisabled}
			inputValue={currentSearchInput}
			onInputChange={handleInputChange}
			noOptionsMessage={currentSearchInput ? undefined : () => noOptionsMessage}
			canClearFilter={canClearFilter}
			onChange={handleChange}
			onClearOptions={onClearOptions}
			formatOptionLabel={formatOptionLabel}
			options={options}
			isOptionSelected={(option) => option.isSelected}
			shouldCloseMenuOnTab={false}
			components={{
				Control,
				MenuList,
				...(checkboxIndicators ? { Option: CheckboxOption } : {}),
			}}
			popperProps={{
				modifiers: [
					{ name: 'offset', options: { offset: [0, 8] } },
					{
						name: 'preventOverflow',
						options: {
							rootBoundary: 'viewport',
						},
						enabled: false,
					},
				],
			}}
			target={({ isOpen, ...triggerProps }) => {
				let firstSelectedLabel = getFirstSelectedLabel?.(firstSelected) || firstSelected?.label;
				let labelElement: ReactElement | string = label;
				if (typeof label === 'string') {
					labelElement = (
						<>
							{label}
							{firstSelectedLabel
								? `: ${firstSelectedLabel}${numSelectedOptions > 1 ? `,` : ''}`
								: ''}
						</>
					);
				}

				const isSelected = isOpen || numSelectedOptions > 0;
				return (
					<FilterButton
						testId={testId ? `${testId}-button` : undefined}
						otherProps={triggerProps}
						isSelected={isSelected}
						label={labelElement}
						filterOrControlId={filterOrControlId}
						selectionBadge={
							numSelectedOptions > 1 ? (
								<Badge appearance="primary">{`+${numSelectedOptions - 1}`}</Badge>
							) : undefined
						}
					/>
				);
			}}
		/>
	);
};

export default SelectFilter;
